import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface DocCustomCheckboxChangeEvent {
  checked: boolean;
  originalEvent?: Event;
}

@Component({
  selector: 'doc-custom-checkbox',
  standalone: true,
  imports: [CommonModule],
  template: `
    <label
      class="doc-custom-checkbox"
      [class.disabled]="disabled"
      [attr.for]="inputId || null">
      <input
        type="checkbox"
        class="doc-custom-checkbox-input"
        [id]="inputId || null"
        [checked]="checked"
        [disabled]="disabled"
        [attr.indeterminate]="indeterminate ? true : null"
        [attr.aria-checked]="indeterminate ? 'mixed' : checked"
        [attr.aria-disabled]="disabled"
        role="checkbox"
        (change)="onInputChange($event)" />
      <span
        class="doc-custom-checkbox-box"
        [class.checked]="checked"
        [class.indeterminate]="indeterminate"></span>
    </label>
  `,
  styleUrl: './doc-custom-checkbox.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DocCustomCheckboxComponent),
      multi: true,
    }],
})
export class DocCustomCheckboxComponent implements ControlValueAccessor {
  /** PrimeNG-compatible flag; boolean mode is always used. */
  @Input() binary = true;
  @Input() inputId = '';
  @Input() styleClass = '';
  @Input() indeterminate = false;

  @Input() disabled = false;

  @Output() onChange = new EventEmitter<DocCustomCheckboxChangeEvent>();

  @HostBinding('class')
  get hostClass(): string {
    return ['doc-custom-checkbox-host', this.styleClass].filter(Boolean).join(' ');
  }

  checked = false;

  onInputChange(event: Event): void {
    if (this.disabled) {
      event.preventDefault();
      return;
    }

    const input = event.target as HTMLInputElement;
    this.indeterminate = false;
    this.checked = input.checked;
    this.emitChange(event);
  }

  private emitChange(event?: Event): void {
    this.onChange.emit({ checked: this.checked, originalEvent: event });
    this.onChangeFn(this.checked);
    this.onTouched();
  }

  private onChangeFn: (value: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: unknown): void {
    this.checked = value === true;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
